import { memo } from "react";
import { CampaignTypes } from "types/campaigns";
import { SelectedFilter } from "types/filters";
import { AchievementName } from "./Values/AchievementName";
import { Boolean } from "./Values/Boolean";
import { BulkActions } from "./Values/BulkActions";
import { CampaignName } from "./Values/CampaignName";
import { CampaignTrigger } from "./Values/CampaignTrigger";
import { Channels } from "./Values/Channels";
import { Country } from "./Values/Country";
import { DateInput } from "./Values/DateInput";
import { DateTime } from "./Values/DateTime";
import { EventNameFilter } from "./Values/EventName";
import { EventSystemIdentifier } from "./Values/EventSystemIndentifier";
import { ExportStatus } from "./Values/ExportStatus";
import { ExportType } from "./Values/ExportType";
import { Gender } from "./Values/Gender";
import { ImportDetailsStatus } from "./Values/ImportDetailsStatus";
import { ImportType } from "./Values/ImportType";
import { LoyaltyCardNumber } from "./Values/LoyaltyCardNumber";
import { MemberEmail } from "./Values/MemberEmail";
import { MemberPhone } from "./Values/MemerPhone";
import { MultiInput } from "./Values/MultiInput";
import { MultiWithModal } from "./Values/MultiWithModal";
import { Number } from "./Values/Number";
import { PointsTransferType } from "./Values/PointsTransferType";
import { RedemptionCodeStatus } from "./Values/RedemptionCodeStatus";
import { RewardCategories } from "./Values/RewardCategories";
import { RewardFullfilmentStatus } from "./Values/RewardFullfilmentStatus";
import { RewardName } from "./Values/RewardName";
import { RewardType } from "./Values/RewardType";
import { Segments } from "./Values/Segments";
import { Tenant } from "./Values/Tenant";
import { Text } from "./Values/Text";
import { TierNameId } from "./Values/TierNameId";
import { Tiers } from "./Values/Tiers";
import { Timeline } from "./Values/Timeline";
import { TransactionDocumentNumber } from "./Values/TransactionDocumentNumber";
import { TransactionDocumentType } from "./Values/TransactionDocumentType";
import { WalletType } from "./Values/WalletType";
import { WalletTypeName } from "./Values/WalletTypeName";
import { WebhookName } from "./Values/WebhookName";

interface Props {
  filter: SelectedFilter;
  setFieldValue?: (field: string, value: any) => void;
  index: number;
  size: "small" | "medium" | "inline";
  onChange?: () => void;
  setIsEditingFilters?: (val: boolean) => void;
}

const getCampaignType = (
  type:
    | "direct-campaign-name-filter"
    | "time-campaign-name-filter"
    | "referral-name-filter"
    | "campaign-name-filter",
) => {
  switch (type) {
    case "direct-campaign-name-filter":
      return CampaignTypes.DIRECT;
    case "time-campaign-name-filter":
      return CampaignTypes.TIME;
    case "referral-name-filter":
      return CampaignTypes.REFERRAL;
    case "campaign-name-filter":
      return undefined;
  }
};

export const Values = memo(
  ({
    filter,
    size,
    index,
    onChange,
    setFieldValue,
    setIsEditingFilters,
  }: Props) => {
    switch (filter.type) {
      case "boolean":
      case "boolean-status":
      case "boolean-yes-no":
        return (
          <Boolean
            size={size}
            index={index}
            type={filter.type}
            onChange={onChange}
          />
        );
      case "datetime":
        return (
          <DateTime
            size={size}
            index={index}
            onChange={onChange}
            dataHjSuppress={filter.dataHjSuppress}
          />
        );
      case "date":
        return (
          <DateInput
            size={size}
            index={index}
            onChange={onChange}
            dataHjSuppress={filter.dataHjSuppress}
          />
        );
      case "multiwithmodal":
        return (
          <MultiWithModal
            size={size}
            index={index}
            onChange={onChange}
            setFieldValue={setFieldValue}
            filter={filter}
            setIsEditingFilters={setIsEditingFilters}
          />
        );
      case "direct-campaign-name-filter":
      case "time-campaign-name-filter":
      case "referral-name-filter":
      case "campaign-name-filter":
        const campaignType = getCampaignType(filter.type);

        return (
          <CampaignName
            size={size}
            index={index}
            type={campaignType}
            onChange={onChange}
          />
        );
      case "text":
        return (
          <Text
            size={size}
            index={index}
            onChange={onChange}
            dataHjSuppress={filter.dataHjSuppress}
          />
        );
      case "direct-trigger-filter":
      case "referral-trigger-filter":
        return (
          <CampaignTrigger size={size} index={index} onChange={onChange} />
        );
      case "number":
        return (
          <Number
            size={size}
            index={index}
            onChange={onChange}
            dataHjSuppress={filter.dataHjSuppress}
          />
        );
      case "country-filter":
        return <Country size={size} index={index} onChange={onChange} />;
      case "member-email-filter":
        return (
          <MemberEmail
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "gender":
        return <Gender size={size} index={index} onChange={onChange} />;
      case "tier-name-with-id-filter":
        return <TierNameId size={size} index={index} onChange={onChange} />;
      case "tiers":
        return <Tiers size={size} index={index} onChange={onChange} multiple />;
      case "member-phone-filter":
        return (
          <MemberPhone
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "timeline-event-type":
        return <Timeline size={size} index={index} onChange={onChange} />;
      case "wallet-type-code-filter":
        return (
          <WalletType
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "achievement-name":
        return (
          <AchievementName
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "bulk-actions":
        return <BulkActions size={size} index={index} onChange={onChange} />;
      case "event-name-filter":
        return (
          <EventNameFilter size={size} index={index} onChange={onChange} />
        );
      case "event-system-identifier-filter":
        return (
          <EventSystemIdentifier
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "reward-categories":
        return (
          <RewardCategories size={size} index={index} onChange={onChange} />
        );
      case "reward-name-filter":
        return <RewardName size={size} index={index} onChange={onChange} />;
      case "reward-types-filter":
      case "reward-types-filter_eq":
        return <RewardType size={size} index={index} onChange={onChange} />;
      case "segments":
        return <Segments size={size} index={index} onChange={onChange} />;
      case "reward-fulfillment-status":
        return (
          <RewardFullfilmentStatus
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "member-loyalty-card-number":
        return (
          <LoyaltyCardNumber
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "wallet-type-name-filter":
        return <WalletTypeName size={size} index={index} onChange={onChange} />;
      case "tenant-name":
        return <Tenant size={size} index={index} onChange={onChange} />;
      case "import-type":
        return <ImportType size={size} index={index} onChange={onChange} />;
      case "export-type":
        return <ExportType size={size} index={index} onChange={onChange} />;
      case "export-status":
        return <ExportStatus size={size} index={index} onChange={onChange} />;
      case "points-transfers-types":
        return (
          <PointsTransferType size={size} index={index} onChange={onChange} />
        );
      case "channel-filter":
        return <Channels size={size} index={index} onChange={onChange} />;
      case "transaction-document-number":
        return (
          <TransactionDocumentNumber
            size={size}
            index={index}
            onChange={onChange}
            filter={filter}
          />
        );
      case "transaction-document-type":
        return (
          <TransactionDocumentType
            size={size}
            index={index}
            onChange={onChange}
          />
        );
      case "multiinput":
        return <MultiInput size={size} index={index} onChange={onChange} />;
      case "webhook-name-filter":
        return <WebhookName size={size} index={index} onChange={onChange} />;
      case "tier-name-filter":
        return (
          <Tiers
            size={size}
            index={index}
            onChange={onChange}
            multiple={false}
          />
        );
      case "redemption-code-status":
        return (
          <RedemptionCodeStatus size={size} index={index} onChange={onChange} />
        );
      case "import-details-status":
        return (
          <ImportDetailsStatus size={size} index={index} onChange={onChange} />
        );
      case "segment-single":
        return (
          <Segments
            size={size}
            index={index}
            onChange={onChange}
            multiple={false}
          />
        );
      default:
        return null;
    }
  },
);
